import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './AppointmentsManager.scss';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_OPTIONS = ['all', 'pending', 'confirmed', 'cancelled'];

const STATUS_BADGE = {
  pending:   'badge bg-warning text-dark',
  confirmed: 'badge bg-success',
  cancelled: 'badge bg-danger',
};

function formatRefId(id) {
  if (!id) return '';
  return 'DENT-' + id.slice(-6).toUpperCase();
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmt12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

const EMPTY_CREATE = {
  date: '', slotId: '', patientName: '', patientEmail: '', patientPhone: '', message: '',
};

function exportToCsv(appointments) {
  const headers = ['Ref', 'Patient Name', 'Email', 'Phone', 'Service', 'Department', 'Appt Date', 'Appt Time', 'Doctor', 'Status', 'Booked On'];
  const rows = appointments.map(a => [
    formatRefId(a._id),
    a.patient_name || '',
    a.patient_email || '',
    a.patient_phone || '',
    a.service || '',
    a.department || '',
    a.slot_date || '',
    a.slot_time ? fmt12(a.slot_time) : '',
    a.slot_doctor ? `Dr. ${a.slot_doctor}` : '',
    a.status || '',
    a.created_at ? new Date(a.created_at).toLocaleDateString() : '',
  ].map(v => `"${String(v).replace(/"/g, '""')}"`));
  const csv = [headers.map(h => `"${h}"`), ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dentalist-appointments-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function AppointmentsManager() {
  const { authFetch } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]             = useState('');
  const [updatingId, setUpdatingId]     = useState(null);

  // Sorting
  const [sort, setSort] = useState({ col: 'slot_date', dir: 'desc' });

  // Detail modal
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [detailNotes, setDetailNotes]   = useState('');
  const [savingNotes, setSavingNotes]   = useState(false);
  const [notesError, setNotesError]     = useState('');
  const [notesSaved, setNotesSaved]     = useState(false);

  // ── Create modal ────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate]     = useState(false);
  const [createForm, setCreateForm]     = useState(EMPTY_CREATE);
  const [createSlots, setCreateSlots]   = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [createError, setCreateError]   = useState('');
  const [createSaving, setCreateSaving] = useState(false);

  // Lock body scroll when any modal is open
  useEffect(() => {
    document.body.style.overflow = (showCreate || selectedAppt) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showCreate, selectedAppt]);

  // Fetch available slots when date changes
  useEffect(() => {
    if (!createForm.date) { setCreateSlots([]); return; }
    let cancelled = false;
    setLoadingSlots(true);
    setCreateSlots([]);
    setCreateForm(f => ({ ...f, slotId: '' }));
    fetch(`${API}/api/slots/?date=${createForm.date}`)
      .then(r => r.json())
      .then(data => { if (!cancelled) setCreateSlots(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setCreateSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [createForm.date]);

  function openCreate() {
    setCreateForm(EMPTY_CREATE);
    setCreateSlots([]);
    setCreateError('');
    setShowCreate(true);
  }

  function closeCreate() {
    setShowCreate(false);
    setCreateForm(EMPTY_CREATE);
    setCreateSlots([]);
    setCreateError('');
  }

  async function submitCreate(e) {
    e.preventDefault();
    setCreateError('');
    const slot = createSlots.find(s => (s._id || s.id) === createForm.slotId);
    if (!slot) { setCreateError('Please select a time slot.'); return; }
    setCreateSaving(true);
    try {
      const res = await authFetch(`${API}/api/appointments/`, {
        method: 'POST',
        body: JSON.stringify({
          slot_id:       createForm.slotId,
          patient_name:  createForm.patientName,
          patient_email: createForm.patientEmail,
          patient_phone: createForm.patientPhone,
          service:       slot.service,
          department:    slot.department,
          message:       createForm.message,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(
          Array.isArray(detail) ? detail.map(e => e.msg).join('; ')
            : (typeof detail === 'string' ? detail : 'Failed to create appointment')
        );
      }
      setSuccess('Appointment created successfully.');
      setTimeout(() => setSuccess(''), 3000);
      closeCreate();
      loadAppointments();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateSaving(false);
    }
  }

  // ── Appointments list ────────────────────────────────────────────────────────
  const loadAppointments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status_filter', statusFilter);
      const res = await authFetch(`${API}/api/appointments/?${params}`);
      if (!res.ok) throw new Error('Failed to load appointments');
      setAppointments(await res.json());
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Unable to connect to the server.'
        : err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, statusFilter]);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  async function updateStatus(apptId, newStatus) {
    setUpdatingId(apptId);
    setError('');
    try {
      const res = await authFetch(`${API}/api/appointments/${apptId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update status');
      }
      setAppointments(prev =>
        prev.map(a => a._id === apptId ? { ...a, status: newStatus } : a)
      );
      if (selectedAppt && selectedAppt._id === apptId) {
        setSelectedAppt(prev => ({ ...prev, status: newStatus }));
      }
      setSuccess(`Status updated to "${newStatus}".`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  // ── Detail modal ─────────────────────────────────────────────────────────────
  function openDetail(appt) {
    setSelectedAppt(appt);
    setDetailNotes(appt.notes || '');
    setNotesError('');
    setNotesSaved(false);
  }

  function closeDetail() {
    setSelectedAppt(null);
    setDetailNotes('');
    setNotesError('');
    setNotesSaved(false);
  }

  async function saveNotes() {
    if (!selectedAppt) return;
    setSavingNotes(true);
    setNotesError('');
    setNotesSaved(false);
    try {
      const res = await authFetch(`${API}/api/appointments/${selectedAppt._id}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: detailNotes }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to save notes');
      }
      setAppointments(prev =>
        prev.map(a => a._id === selectedAppt._id ? { ...a, notes: detailNotes } : a)
      );
      setSelectedAppt(prev => ({ ...prev, notes: detailNotes }));
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (err) {
      setNotesError(err.message);
    } finally {
      setSavingNotes(false);
    }
  }

  // ── Sorting + filtering ───────────────────────────────────────────────────────
  function toggleSort(col) {
    setSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'desc' }
    );
  }

  function SortIcon({ col }) {
    if (sort.col !== col) return <span className="apm-sort-icon apm-sort-icon--dim">↕</span>;
    return <span className="apm-sort-icon">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  }

  const filtered = (() => {
    const base = appointments.filter(a => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        a.patient_name?.toLowerCase().includes(q) ||
        a.patient_email?.toLowerCase().includes(q) ||
        formatRefId(a._id).toLowerCase().includes(q)
      );
    });
    return [...base].sort((a, b) => {
      let va, vb;
      if (sort.col === 'slot_date') {
        va = a.slot_date || '';
        vb = b.slot_date || '';
      } else if (sort.col === 'patient_name') {
        va = (a.patient_name || '').toLowerCase();
        vb = (b.patient_name || '').toLowerCase();
      } else if (sort.col === 'status') {
        const order = { pending: 0, confirmed: 1, cancelled: 2 };
        va = order[a.status] ?? 3;
        vb = order[b.status] ?? 3;
      } else {
        return 0;
      }
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const selectedSlot = createSlots.find(s => (s._id || s.id) === createForm.slotId);

  return (
    <div className="apm-page">
      <div className="apm-header">
        <h2 className="apm-title">Appointments</h2>
        <div className="apm-header-actions">
          <button
            className="btn apm-export-btn"
            onClick={() => exportToCsv(filtered)}
            title={`Export ${filtered.length} appointment(s) to CSV`}
            disabled={filtered.length === 0}
          >
            ↓ Export CSV
          </button>
          <button className="btn apm-create-btn" onClick={openCreate}>
            + New Appointment
          </button>
          <button
            className="btn apm-refresh-btn"
            onClick={loadAppointments}
            disabled={loading}
          >
            {loading ? '↻ Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error   && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filters */}
      <div className="apm-filters">
        <div className="apm-filter-tabs">
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              className={`apm-filter-tab ${statusFilter === s ? 'apm-filter-tab--active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <input
          type="search"
          className="form-control apm-search"
          placeholder="Search by name, email or ref…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="apm-loading">
          <div className="spinner-border" style={{ color: '#583FBC' }} role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="apm-table-wrap">
          <table className="table table-hover apm-table">
            <thead>
              <tr>
                <th>Ref</th>
                <th
                  className="apm-th-sortable"
                  onClick={() => toggleSort('patient_name')}
                >
                  Patient <SortIcon col="patient_name" />
                </th>
                <th>Email</th>
                <th>Service</th>
                <th>Department</th>
                <th
                  className="apm-th-sortable"
                  onClick={() => toggleSort('slot_date')}
                >
                  Appt. Date <SortIcon col="slot_date" />
                </th>
                <th
                  className="apm-th-sortable"
                  onClick={() => toggleSort('status')}
                >
                  Status <SortIcon col="status" />
                </th>
                <th>Update Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No appointments found.
                  </td>
                </tr>
              ) : (
                filtered.map(appt => (
                  <tr
                    key={appt._id}
                    className="apm-row-clickable"
                    onClick={() => openDetail(appt)}
                    title="Click to view details"
                  >
                    <td className="apm-ref">{formatRefId(appt._id)}</td>
                    <td className="apm-name">{appt.patient_name}</td>
                    <td className="apm-email">
                      {appt.patient_email}
                      {appt.patient_phone && <div className="apm-phone">{appt.patient_phone}</div>}
                    </td>
                    <td>{appt.service}</td>
                    <td>{appt.department}</td>
                    <td className="apm-date">
                      <div>{appt.slot_date || '—'}</div>
                      {appt.slot_time && <div className="apm-time-sub">{fmt12(appt.slot_time)}</div>}
                    </td>
                    <td>
                      <span className={STATUS_BADGE[appt.status] || 'badge bg-secondary'}>
                        {appt.status}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <select
                        className="form-select form-select-sm apm-status-select"
                        value={appt.status}
                        disabled={updatingId === appt._id}
                        onChange={e => updateStatus(appt._id, e.target.value)}
                        aria-label={`Change status for ${appt.patient_name}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className="apm-count">
              Showing {filtered.length} of {appointments.length} appointment{appointments.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* ── Detail Modal ── */}
      {selectedAppt && (
        <div className="apm-overlay" onClick={e => e.target === e.currentTarget && closeDetail()}>
          <div className="apm-modal apm-detail-modal">
            <div className="apm-modal-header">
              <div>
                <h5 className="apm-modal-title">Appointment Details</h5>
                <div className="apm-detail-ref">{formatRefId(selectedAppt._id)}</div>
              </div>
              <button className="apm-modal-close" onClick={closeDetail} aria-label="Close">×</button>
            </div>

            <div className="apm-modal-body">
              {/* Status row */}
              <div className="apm-detail-status-row">
                <span className={`${STATUS_BADGE[selectedAppt.status] || 'badge bg-secondary'} apm-detail-badge`}>
                  {selectedAppt.status}
                </span>
                <select
                  className="form-select form-select-sm apm-status-select"
                  value={selectedAppt.status}
                  disabled={updatingId === selectedAppt._id}
                  onChange={e => updateStatus(selectedAppt._id, e.target.value)}
                  aria-label="Change appointment status"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Appointment info grid */}
              <div className="apm-detail-grid">
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Date</div>
                  <div className="apm-detail-value">{selectedAppt.slot_date || '—'}</div>
                </div>
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Time</div>
                  <div className="apm-detail-value">{fmt12(selectedAppt.slot_time) || '—'}</div>
                </div>
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Doctor</div>
                  <div className="apm-detail-value">
                    {selectedAppt.slot_doctor ? `Dr. ${selectedAppt.slot_doctor}` : '—'}
                  </div>
                </div>
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Service</div>
                  <div className="apm-detail-value">{selectedAppt.service}</div>
                </div>
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Department</div>
                  <div className="apm-detail-value">{selectedAppt.department}</div>
                </div>
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Booked On</div>
                  <div className="apm-detail-value">{formatDate(selectedAppt.created_at)}</div>
                </div>
              </div>

              <div className="apm-detail-divider" />

              {/* Patient info */}
              <div className="apm-detail-grid">
                <div className="apm-detail-item apm-detail-item--full">
                  <div className="apm-detail-label">Patient Name</div>
                  <div className="apm-detail-value">{selectedAppt.patient_name}</div>
                </div>
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Email</div>
                  <div className="apm-detail-value apm-detail-value--muted">{selectedAppt.patient_email}</div>
                </div>
                <div className="apm-detail-item">
                  <div className="apm-detail-label">Phone</div>
                  <div className="apm-detail-value">{selectedAppt.patient_phone || '—'}</div>
                </div>
              </div>

              {selectedAppt.message && (
                <div className="apm-detail-message">
                  <div className="apm-detail-label">Patient Note</div>
                  <div className="apm-detail-message-text">{selectedAppt.message}</div>
                </div>
              )}

              <div className="apm-detail-divider" />

              {/* Admin notes */}
              <div className="mb-1">
                <label className="form-label apm-detail-label" htmlFor="apm-notes">
                  Admin Notes
                </label>
                <textarea
                  id="apm-notes"
                  className="form-control apm-notes-textarea"
                  rows={3}
                  placeholder="Add internal notes about this appointment…"
                  value={detailNotes}
                  onChange={e => setDetailNotes(e.target.value)}
                />
                {notesError && <div className="apm-notes-error">{notesError}</div>}
                {notesSaved && <div className="apm-notes-saved">Notes saved.</div>}
              </div>
            </div>

            <div className="apm-modal-footer">
              <button type="button" className="btn apm-cancel-btn" onClick={closeDetail}>
                Close
              </button>
              <button
                type="button"
                className="btn apm-save-btn"
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Saving…' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Appointment Modal ── */}
      {showCreate && (
        <div className="apm-overlay" onClick={e => e.target === e.currentTarget && closeCreate()}>
          <div className="apm-modal">
            <div className="apm-modal-header">
              <h5 className="apm-modal-title">New Appointment</h5>
              <button className="apm-modal-close" onClick={closeCreate}>×</button>
            </div>

            <form id="apm-create-form" onSubmit={submitCreate}>
              <div className="apm-modal-body">
                {createError && (
                  <div className="alert alert-danger apm-form-error">{createError}</div>
                )}

                <div className="mb-3">
                  <label className="form-label">Date <span className="apm-required">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    value={createForm.date}
                    min={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()}
                    required
                    onChange={e => setCreateForm(f => ({ ...f, date: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Time Slot <span className="apm-required">*</span></label>
                  {loadingSlots ? (
                    <div className="apm-slots-loading">Loading slots…</div>
                  ) : (
                    <select
                      className="form-select"
                      value={createForm.slotId}
                      required
                      disabled={!createForm.date || createSlots.length === 0}
                      onChange={e => setCreateForm(f => ({ ...f, slotId: e.target.value }))}
                    >
                      <option value="">
                        {!createForm.date
                          ? 'Pick a date first'
                          : createSlots.length === 0
                            ? 'No slots available on this date'
                            : 'Select a slot…'}
                      </option>
                      {createSlots.map(s => (
                        <option key={s._id || s.id} value={s._id || s.id}>
                          {fmt12(s.start_time)} – {fmt12(s.end_time)} · Dr. {s.doctor_name} · {s.service}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {selectedSlot && (
                  <div className="apm-slot-info">
                    <span>🦷 {selectedSlot.service}</span>
                    <span>🏥 {selectedSlot.department}</span>
                    <span>👨‍⚕️ Dr. {selectedSlot.doctor_name}</span>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Patient Name <span className="apm-required">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Full name"
                    value={createForm.patientName}
                    required
                    minLength={2}
                    onChange={e => setCreateForm(f => ({ ...f, patientName: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Patient Email <span className="apm-required">*</span></label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="email@example.com"
                    value={createForm.patientEmail}
                    required
                    onChange={e => setCreateForm(f => ({ ...f, patientEmail: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Phone Number <span className="apm-optional">(optional)</span></label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="+91 98765 43210"
                    value={createForm.patientPhone}
                    onChange={e => setCreateForm(f => ({ ...f, patientPhone: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes <span className="apm-optional">(optional)</span></label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Reason for visit or additional notes…"
                    value={createForm.message}
                    onChange={e => setCreateForm(f => ({ ...f, message: e.target.value }))}
                  />
                </div>
              </div>

              <div className="apm-modal-footer">
                <button type="button" className="btn apm-cancel-btn" onClick={closeCreate}>
                  Cancel
                </button>
                <button type="submit" className="btn apm-save-btn" disabled={createSaving}>
                  {createSaving ? 'Creating…' : 'Create Appointment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
